// src/components/common/LoadingSkeletons.js
import React from 'react';
import { Box, Skeleton, Paper, Grid } from '@mui/material';

// Table skeleton loader
export const TableSkeleton = ({ rows = 5, columns = 5 }) => (
  <Box>
    {[...Array(rows)].map((_, rowIndex) => (
      <Box
        key={rowIndex}
        display="flex"
        gap={2}
        sx={{
          py: 1.5,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: rowIndex % 2 === 0 ? 'grey.50' : 'white',
        }}
      >
        {[...Array(columns)].map((_, colIndex) => (
          <Skeleton
            key={colIndex}
            variant="text"
            width={`${100 / columns}%`}
            height={24}
          />
        ))}
      </Box>
    ))}
  </Box>
);

// Card skeleton loader
export const CardSkeleton = ({ count = 4 }) => (
  <Grid container spacing={3}>
    {[...Array(count)].map((_, index) => (
      <Grid item xs={12} sm={6} md={3} key={index}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="80%" height={40} sx={{ my: 1 }} />
              <Skeleton variant="text" width="50%" height={20} />
            </Box>
            <Skeleton variant="circular" width={56} height={56} />
          </Box>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

// List skeleton loader
export const ListSkeleton = ({ items = 5 }) => (
  <Box>
    {[...Array(items)].map((_, index) => (
      <Box
        key={index}
        display="flex"
        gap={2}
        alignItems="center"
        sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Skeleton variant="circular" width={40} height={40} />
        <Box flex={1}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="80%" height={20} />
        </Box>
      </Box>
    ))}
  </Box>
);

// Dashboard skeleton loader
export const DashboardSkeleton = () => (
  <Box sx={{ p: 3 }}>
    {/* Header skeleton */}
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box flex={1}>
          <Skeleton variant="text" width="40%" height={40} />
          <Skeleton variant="text" width="60%" height={24} sx={{ mt: 1 }} />
        </Box>
        <Skeleton variant="rectangular" width={100} height={40} />
      </Box>
    </Paper>

    {/* Stats cards skeleton */}
    <CardSkeleton count={4} />

    {/* Content area skeleton */}
    <Paper sx={{ p: 3, mt: 4 }}>
      <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={300} />
    </Paper>
  </Box>
);

// Form skeleton loader
export const FormSkeleton = ({ fields = 6 }) => (
  <Box>
    {[...Array(fields)].map((_, index) => (
      <Box key={index} sx={{ mb: 3 }}>
        <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" width="100%" height={56} />
      </Box>
    ))}
    <Box display="flex" gap={2} sx={{ mt: 4 }}>
      <Skeleton variant="rectangular" width={120} height={40} />
      <Skeleton variant="rectangular" width={120} height={40} />
    </Box>
  </Box>
);

// Chart skeleton loader
export const ChartSkeleton = ({ height = 300 }) => (
  <Box>
    <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
    <Skeleton variant="rectangular" width="100%" height={height} />
  </Box>
);